import { z } from 'zod';

/**
 * Server-driven module pages (G21, ADR-0035) — the interim UI story for
 * out-of-tree modules.
 *
 * An out-of-tree module cannot ship React components into the panel's
 * pre-built SPA bundle, so instead of a bespoke page it describes its page
 * to the server: a set of read-only info rows and an optional config form
 * built from typed fields (a small, closed JSON-schema dialect). The web
 * shell renders any module's page generically at `/m/<key>` from this DTO,
 * and posts edited values back. First-party modules that want richer UIs
 * still ship in-tree pages; this is the zero-friction default, not a ceiling.
 */

/** The field kinds the generic renderer understands. */
export const MODULE_FIELD_TYPES = ['text', 'textarea', 'number', 'boolean', 'select'] as const;
export type ModuleFieldType = (typeof MODULE_FIELD_TYPES)[number];

export interface ModuleSelectOption {
  value: string;
  label: string;
}

/** One control in a server-driven config form. */
export interface ModuleFormField {
  key: string;
  label: string;
  type: ModuleFieldType;
  /** Helper text under the control. */
  help?: string;
  placeholder?: string;
  required?: boolean;
  /** Options for `type: 'select'`. */
  options?: ModuleSelectOption[];
}

/** A read-only label/value row rendered above the form. */
export interface ModulePageInfoRow {
  label: string;
  value: string;
}

/**
 * A module's page as the web shell renders it. `fields` empty (and
 * `submitLabel` absent) means a read-only page — info rows only.
 */
export interface ModulePageDto {
  title: string;
  description?: string;
  info?: ModulePageInfoRow[];
  fields: ModuleFormField[];
  /** Current values keyed by field key. */
  values: Record<string, string | number | boolean | null>;
  /** Save-button label; omit for a read-only page (no form submit). */
  submitLabel?: string;
}

/**
 * The shape a module page's save endpoint receives. Values are validated
 * loosely here (types); the module does field-level validation itself.
 */
export const moduleFormValuesSchema = z.record(
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);
export type ModuleFormValues = z.infer<typeof moduleFormValuesSchema>;
